import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Palette, Package } from "lucide-react";
import { appsApi, errMsg, notifyError } from "@/lib/appsApi";
import { useFileActions } from "@/lib/useFileActions";
import { FileList } from "@/components/apps/FileList";
import { UploadForm } from "@/components/apps/UploadForm";
import { TesterManager } from "@/components/apps/TesterManager";
import { SharedFiles } from "@/components/apps/SharedFiles";
import { BundleRules } from "@/components/apps/BundleRules";
import { TabsBar } from "@/components/apps/TabsBar";
import { StorageBrowser } from "@/components/apps/StorageBrowser";
import { useTabs } from "@/lib/useTabs";

export const AppsPortal = ({ user, onSignOut }) => {
  const [files, setFiles] = useState([]);
  const [rules, setRules] = useState(null);
  const [fileTypes, setFileTypes] = useState([]);
  const [typeNames, setTypeNames] = useState([]);
  const isAdmin = user?.role === "admin";
  const typeOrder = isAdmin && fileTypes.length ? fileTypes.map((x) => x.name) : typeNames;
  const t = useTabs();
  const shown = files.filter((f) => f.tab_id === t.active);

  const load = () => appsApi.get("/files").then((r) => setFiles(Array.isArray(r?.data) ? r.data : [])).catch((err) => notifyError(errMsg(err, "Loading the file list")));

  useEffect(() => {
    load();
    appsApi.get("/file-types").then((r) => setTypeNames(Array.isArray(r?.data?.names) ? r.data.names : [])).catch(() => setTypeNames([]));
    if (isAdmin) appsApi.get("/admin/bundle-rules").then((r) => setRules(r?.data ?? null)).catch((err) => notifyError(errMsg(err, "Loading the bundle checklist")));
    if (isAdmin) appsApi.get("/admin/file-types").then((r) => setFileTypes(Array.isArray(r?.data?.types) ? r.data.types : [])).catch((err) => notifyError(errMsg(err, "Loading the file types")));
  }, []);

  const processing = files.some((f) => f.status === "processing");
  useEffect(() => {
    if (!processing) return undefined;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [processing]);

  const { notify, flag, stop, remove, patch } = useFileActions({ setFiles, load, onChange: () => t.reload() });

  const move = async (id, tabId) => {
    try {
      const r = await appsApi.post(`/files/${id}/move`, { tab_id: tabId });
      patch(id, r?.data);
      t.reload();
      toast.success(`Moved to ${t.tabs.find((x) => x.id === tabId)?.name ?? "the other tab"}`);
    } catch (err) {
      notifyError(errMsg(err, "Moving the file"));
    }
  };

  return (
    <div data-testid="apps-portal">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">{isAdmin ? "Admin" : "Tester"} · {user?.email}</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">Test builds &amp; files</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/apps/bundles" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink hover:border-sky-600 hover:text-sky-700" data-testid="apps-bundles-link">
            <Package className="h-4 w-4" /> Data packages
          </Link>
          <Link to="/apps/brand" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink hover:border-sky-600 hover:text-sky-700" data-testid="apps-brand-kit-link">
            <Palette className="h-4 w-4" /> Brand kit
          </Link>
          <button onClick={onSignOut} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink hover:border-sky-600 hover:text-sky-700" data-testid="apps-signout-button">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
      <div className="mt-8">
        <TabsBar tabs={t.tabs} active={t.active} onSelect={t.setActive} isAdmin={isAdmin} onAdd={t.add} onRename={t.rename} onDelete={t.remove} />
      </div>
      {t.loaded && t.tabs.length === 0 ? (
        <p className="mt-6 rounded-3xl border border-dashed border-sky-200 bg-white p-10 text-center text-slate-600" data-testid="apps-no-tabs">No sections have been shared with you yet. Ask an admin to enable the ones you're testing.</p>
      ) : (
        <div className="mt-5"><FileList files={shown} tabs={t.tabs} typeOrder={typeOrder} isAdmin={isAdmin} onFlag={flag} onNotify={notify} onDelete={remove} onMove={move} onStop={stop} /></div>
      )}
      {isAdmin && (
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <UploadForm existing={files} tabs={t.tabs} activeTab={t.active} fileTypes={fileTypes} onTypesSaved={setFileTypes} requiredPatterns={Array.isArray(rules?.patterns) ? rules.patterns : []}
            onUploaded={(f) => { if (f) { setFiles((l) => [f, ...l]); if (f.tab_id) t.setActive(f.tab_id); t.reload(); } }} />
          <TesterManager me={user} tabs={t.tabs} />
        </div>
      )}
      {isAdmin && <BundleRules rules={rules} onSaved={setRules} />}
      {isAdmin && <SharedFiles />}
      {isAdmin && <StorageBrowser onChanged={load} />}
    </div>
  );
};
